import { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { Alert, List } from 'antd';

import ActionButton from '@src/components/Buttons/ActionButton';
import AuditLogViewer from '@src/components/AuditLogViewer';
import { FSCollections } from '@src/constants/database';
import { SHIFT_STATUS } from '@src/packages/shifts/constants';
import type { ShiftListItem, WorkJourneySummary } from '@src/packages/shifts/types';
import { ALLOWED_ROLES, useConfirmDelete, useIsMobile } from '@src/packages/utils';

import { useShiftContext } from '../context';
import styles from '../shift.module.less';

const LARGE_JOURNEY_LIST_SIZE = 10;

interface ShiftActionsProps {
  record: ShiftListItem;
  onEdit: (record: ShiftListItem) => void;
  onDelete: (record: ShiftListItem) => void | Promise<void>;
}

const ShiftActions = ({ record, onEdit, onDelete }: ShiftActionsProps) => {
  const intl = useIntl();
  const { isMobile } = useIsMobile();
  const { getWorkJourneysByShiftId } = useShiftContext();
  const { showConfirmDelete, ConfirmDeleteModal } = useConfirmDelete();
  const [auditLogShiftId, setAuditLogShiftId] = useState<string | null>(null);
  const [checkingJourneys, setCheckingJourneys] = useState(false);
  const isPending = record.status === SHIFT_STATUS.PENDING;

  const buildBlockedContent = useCallback(
    (workJourneys: WorkJourneySummary[]) => (
      <>
        <Alert
          className="gx-mb-3"
          message={intl.formatMessage({ id: 'shifts.delete.workJourneys' })}
          type="warning"
          showIcon
        />
        <List
          className={
            workJourneys.length > LARGE_JOURNEY_LIST_SIZE ? styles.workJourneysListContainer : ''
          }
          bordered
          header={<b>{intl.formatMessage({ id: 'shifts.delete.associatedJourneys' })}</b>}
          dataSource={workJourneys}
          renderItem={item => <List.Item>{item.name}</List.Item>}
        />
      </>
    ),
    [intl],
  );

  const handleDeleteClick = useCallback(async () => {
    setCheckingJourneys(true);
    const workJourneys = await getWorkJourneysByShiftId(record.id);
    setCheckingJourneys(false);

    if (workJourneys === null) return;

    const isBlocked = workJourneys.length > 0;
    const shiftName = <strong>{record.name || record.id}</strong>;

    showConfirmDelete(
      isBlocked ? null : intl.formatMessage({ id: 'shifts.delete.body' }),
      async () => {
        await onDelete(record);
      },
      {
        title: intl.formatMessage({ id: 'shifts.delete.confirm' }, { name: shiftName }),
        okDanger: true,
        okText: intl.formatMessage({ id: 'general.confirm.delete' }),
        content: isBlocked ? buildBlockedContent(workJourneys) : null,
        okDisabled: isBlocked,
      },
    );
  }, [buildBlockedContent, getWorkJourneysByShiftId, intl, onDelete, record, showConfirmDelete]);

  const buttons = useMemo(
    () => [
      <ActionButton
        key="edit"
        icon="edit"
        tooltipPosition="top"
        disabled={isPending}
        tooltipText={intl.formatMessage({
          id: isPending ? 'shifts.status.pending.tooltip' : 'button.edit',
        })}
        allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_SHIFTS.UPDATE}
        onClick={() => onEdit(record)}
      />,
      <ActionButton
        key="delete"
        icon="delete"
        tooltipPosition="top"
        color="danger"
        disabled={isPending || checkingJourneys}
        tooltipText={intl.formatMessage({
          id: isPending ? 'shifts.status.pending.tooltip' : 'general.confirm.delete',
        })}
        allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_SHIFTS.DELETE}
        onClick={handleDeleteClick}
      />,
      <ActionButton
        key="history"
        icon="history"
        tooltipPosition={isPending ? 'topLeft' : 'top'}
        tooltipText={intl.formatMessage({ id: 'general.history.label' })}
        allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.AUDIT_LOGS.GET}
        onClick={() => setAuditLogShiftId(record.id)}
      />,
    ],
    [checkingJourneys, handleDeleteClick, intl, isPending, onEdit, record],
  );

  return (
    <>
      <ActionButton.Container
        maxVisibleButtons={isMobile ? buttons.length : 0}
        overflowVariant="labeled"
        buttons={buttons}
      />
      <AuditLogViewer
        entityType={FSCollections.WORK_SHIFTS}
        entityId={auditLogShiftId ?? ''}
        recordLabel={record.name || record.id}
        open={!!auditLogShiftId}
        onClose={() => setAuditLogShiftId(null)}
      />
      <ConfirmDeleteModal />
    </>
  );
};

export default ShiftActions;
